import {FormControl, FormField} from 'platform/components';
import {Grid, GridItem, HStack, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {toNumber} from 'ramda-adjunct';

import {VehicleTypeEnumObject} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  MakeSelect,
  ModelSelect,
  useVehicleCatalogue,
  useVehicleModel,
  yearOptions,
} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {VehicleFinancingTemplateFormValues} from '../schemas/vehicleFinancingTemplateFormSchema';
import {keepModelsOfSelectedMakes} from '../utils/keepModelsOfSelectedMakes';
import {TemplateMoneyCurrencies} from '../utils/resolveTemplateMoneyCurrencies';

const SEGMENT_VEHICLE_TYPE = VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR;

const YEAR_OPTIONS = yearOptions.map((option) => toNumber(option.value));

interface VehicleFinancingTemplateSegmentScopeFieldsProps extends TestIdProps {
  control: FormControl<VehicleFinancingTemplateFormValues>;
  formApi: UseFormReturn<VehicleFinancingTemplateFormValues>;
  moneyCurrencies: TemplateMoneyCurrencies;
}

/** The vehicle-segment filters of a template scope: make/model, category, fuel, and the ranges. */
export function VehicleFinancingTemplateSegmentScopeFields(
  props: VehicleFinancingTemplateSegmentScopeFieldsProps
) {
  const selectedMakes = useWatch({control: props.control, name: 'scope.segment.makes'}) ?? [];
  const [vehicleCatalogue, {isLoading: isCatalogueLoading}] =
    useVehicleCatalogue(SEGMENT_VEHICLE_TYPE);
  const [{modelOptions}] = useVehicleModel(SEGMENT_VEHICLE_TYPE, selectedMakes);

  const handleMakesChange = (nextMakes: string | number | string[] | null) => {
    const nextModels = keepModelsOfSelectedMakes({
      selectedModels: props.formApi.getValues('scope.segment.models'),
      selectedMakes: Array.isArray(nextMakes) ? nextMakes : [],
      modelOptions,
    });

    props.formApi.setValue('scope.segment.models', nextModels, {shouldDirty: true});
  };

  return (
    <VStack spacing={4}>
      <Grid columns={2}>
        <GridItem>
          <MakeSelect
            vehicleType={SEGMENT_VEHICLE_TYPE}
            control={props.control}
            name="scope.segment.makes"
            label={i18n.t('entity.vehicle.labels.make')}
            isMultiple
            onChange={handleMakesChange}
            data-testid={suffixTestId('scope-makes', props)}
          />
        </GridItem>
        <GridItem>
          <ModelSelect
            vehicleType={SEGMENT_VEHICLE_TYPE}
            makes={selectedMakes}
            control={props.control}
            name="scope.segment.models"
            label={i18n.t('entity.vehicle.labels.model')}
            isMultiple
            isDisabled={selectedMakes.length === 0}
            data-testid={suffixTestId('scope-models', props)}
          />
        </GridItem>
        <GridItem>
          <FormField
            control={props.control}
            name="scope.segment.vehicleCategories"
            type="multiChoice"
            label={i18n.t('entity.vehicleFinancingTemplate.labels.vehicleCategories')}
            options={vehicleCatalogue.vehicleStyleOptions ?? []}
            placeholder={i18n.t('general.labels.selectOption')}
            isLoading={isCatalogueLoading}
            data-testid={suffixTestId('scope-vehicleCategories', props)}
          />
        </GridItem>
        <GridItem>
          <FormField
            control={props.control}
            name="scope.segment.fuelTypes"
            type="multiChoice"
            label={i18n.t('entity.vehicle.labels.fuel')}
            options={vehicleCatalogue.fuelTypeOptions ?? []}
            placeholder={i18n.t('general.labels.selectOption')}
            isLoading={isCatalogueLoading}
            data-testid={suffixTestId('scope-fuelTypes', props)}
          />
        </GridItem>
      </Grid>

      <Grid columns={3}>
        <GridItem>
          <HStack spacing={4}>
            <FormField
              control={props.control}
              name="scope.segment.yearFrom"
              type="numberInputWithOptions"
              label={i18n.t('entity.vehicleFinancingTemplate.labels.yearFrom')}
              options={YEAR_OPTIONS}
              isClearable
              isFormatNumbersDisabled
              decimalPlaces={0}
              data-testid={suffixTestId('scope-yearFrom', props)}
            />
            <FormField
              control={props.control}
              name="scope.segment.yearTo"
              type="numberInputWithOptions"
              label={i18n.t('entity.vehicleFinancingTemplate.labels.yearTo')}
              options={YEAR_OPTIONS}
              isClearable
              isFormatNumbersDisabled
              decimalPlaces={0}
              data-testid={suffixTestId('scope-yearTo', props)}
            />
          </HStack>
        </GridItem>
        <GridItem>
          <HStack spacing={4}>
            <FormField
              control={props.control}
              name="scope.segment.mileageFrom"
              type="integer"
              label={i18n.t('entity.vehicle.labels.mileageFrom')}
              suffix={i18n.t('general.metric.km')}
              data-testid={suffixTestId('scope-mileageFrom', props)}
            />
            <FormField
              control={props.control}
              name="scope.segment.mileageTo"
              type="integer"
              label={i18n.t('entity.vehicle.labels.mileageTo')}
              suffix={i18n.t('general.metric.km')}
              data-testid={suffixTestId('scope-mileageTo', props)}
            />
          </HStack>
        </GridItem>
        <GridItem>
          <HStack spacing={4}>
            <FormField
              control={props.control}
              name="scope.segment.sellingPriceFrom"
              type="currency"
              currency={props.moneyCurrencies.sellingPriceFrom}
              decimalPlaces={2}
              label={i18n.t('entity.vehicleFinancingTemplate.labels.sellingPriceFrom')}
              data-testid={suffixTestId('scope-sellingPriceFrom', props)}
            />
            <FormField
              control={props.control}
              name="scope.segment.sellingPriceTo"
              type="currency"
              currency={props.moneyCurrencies.sellingPriceTo}
              decimalPlaces={2}
              label={i18n.t('entity.vehicleFinancingTemplate.labels.sellingPriceTo')}
              data-testid={suffixTestId('scope-sellingPriceTo', props)}
            />
          </HStack>
        </GridItem>
      </Grid>
    </VStack>
  );
}
